import { useState, type FormEvent } from 'react'
import {
  ModalShell, Field, inputClass, ServerError,
  CancelButton, PrimaryButton, DangerButton
} from '../modal-form'
import { useCreateService, useUpdateService, useDeleteService } from '../../hooks/useServices'
import { useLocos, useStations, useMeracSync, nextServiceOrdinal } from '../../hooks/useScheduleV2'
import { useConfirm } from '../shared/ConfirmProvider'
import { eachDate, parseIsoDate } from '../../lib/dates'
import type { Service, ServiceCreate, EventListItem } from '../../types'

// ServiceEditor v2 — spec 15 §2. Replaces the v1 ServiceEditor when the
// schedule_v2 flag is on. Differences from v1:
//   - Date picker (override per-day) — populated from eventStart..eventEnd
//   - Booked field with capacity cross-validation
//   - MERAC-sync read-only state for Booked (stubbed off in v2.0)
//   - Loco / From / To as <select> populated from /api/lookup endpoints
//     (falls back to free-text input if catalogue fetch is loading/empty)

const TIME_RE = /^([01][0-9]|2[0-3]):[0-5][0-9]$/

interface Props {
  event: EventListItem
  service: Service | null
  services: Service[]            // for next-ordinal defaulting
  onClose: () => void
}

export function ServiceEditorV2({ event, service, services, onClose }: Props) {
  const isEditing = service !== null
  const create = useCreateService(event.id)
  const update = useUpdateService(event.id)
  const remove = useDeleteService(event.id)
  const { data: locos = [] } = useLocos()
  const { data: stations = [] } = useStations()
  const merac = useMeracSync(event.id)
  const confirm = useConfirm()

  const [serviceLabel, setServiceLabel] = useState(
    service?.serviceLabel ?? `Service ${nextServiceOrdinal(services)}`
  )
  const [timeText, setTimeText] = useState(service?.timeText ?? '09:30')
  const [loco, setLoco] = useState(service?.loco ?? '')
  const [dateOverride, setDateOverride] = useState(service?.dateOverride ?? '')
  const [originStation, setOriginStation] = useState(service?.originStation ?? 'Pickering')
  const [destinationStation, setDestinationStation] = useState(service?.destinationStation ?? 'Newbridge')
  const [capacity, setCapacity] = useState(service?.capacity != null ? String(service.capacity) : '320')
  const [booked, setBooked] = useState(String(service?.booked ?? 0))
  const [notes, setNotes] = useState(service?.notes ?? '')

  const capacityNum = capacity === '' ? null : Number(capacity)
  const bookedNum = Number(booked)

  const errs: Partial<Record<'serviceLabel' | 'timeText' | 'capacity' | 'booked', string>> = {}
  if (!serviceLabel.trim()) errs.serviceLabel = 'Service name is required'
  if (!TIME_RE.test(timeText.trim())) errs.timeText = 'Use HH:MM in 24-hour format'
  if (capacity !== '' && (!Number.isInteger(capacityNum) || (capacityNum ?? -1) < 0)) {
    errs.capacity = 'Capacity must be a non-negative integer'
  }
  if (!Number.isInteger(bookedNum) || bookedNum < 0) {
    errs.booked = 'Booked must be a non-negative integer'
  } else if (capacityNum !== null && bookedNum > capacityNum) {
    errs.booked = 'Booked cannot exceed capacity'
  }

  const isValid = Object.keys(errs).length === 0
  const saving = create.isPending || update.isPending
  const mut = isEditing ? update : create

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!isValid || saving) return
    const body: ServiceCreate = {
      serviceLabel: serviceLabel.trim(),
      timeText: timeText.trim(),
      loco: loco.trim() || null,
      originStation: originStation.trim() || null,
      destinationStation: destinationStation.trim() || null,
      capacity: capacityNum,
      notes: notes.trim() || null,
      sortOrder: service?.sortOrder ?? 0,
      dateOverride: dateOverride || null,
      booked: bookedNum
    }
    if (isEditing && service) {
      update.mutate({ id: service.id, body }, { onSuccess: () => onClose() })
    } else {
      create.mutate(body, { onSuccess: () => onClose() })
    }
  }

  async function handleDelete() {
    if (!service) return
    const ok = await confirm({
      title: 'Delete this service?',
      body: 'Any crew assignments on this service will also be removed.',
      tone: 'destructive',
      confirmLabel: 'Delete service'
    })
    if (!ok) return
    remove.mutate(service.id, { onSuccess: () => onClose() })
  }

  return (
    <ModalShell
      title={isEditing ? 'Edit service' : 'New service'}
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        <>
          {isEditing && (
            <DangerButton onClick={handleDelete} disabled={saving || remove.isPending}>
              {remove.isPending ? 'Deleting…' : 'Delete'}
            </DangerButton>
          )}
          <CancelButton onClick={onClose} disabled={saving || remove.isPending} />
          <PrimaryButton disabled={!isValid || saving || remove.isPending}>
            {saving ? 'Saving…' : isEditing ? 'Save changes' : 'Add service'}
          </PrimaryButton>
        </>
      }
      body={
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Service name" error={errs.serviceLabel}>
              <input
                type="text"
                value={serviceLabel}
                onChange={(e) => setServiceLabel(e.target.value)}
                autoFocus
                maxLength={60}
                className={inputClass(!!errs.serviceLabel)}
              />
            </Field>
            <Field label="Time" hint="HH:MM (24h)" error={errs.timeText}>
              <input
                type="time"
                value={timeText}
                onChange={(e) => setTimeText(e.target.value)}
                className={`${inputClass(!!errs.timeText)} font-mono`}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Loco">
              {locos.length > 0 ? (
                <select
                  value={loco}
                  onChange={(e) => setLoco(e.target.value)}
                  className={inputClass(false)}
                >
                  <option value="">— Pick a loco —</option>
                  {locos.map((l) => (
                    <option key={l.id} value={l.name}>{l.name}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={loco}
                  onChange={(e) => setLoco(e.target.value)}
                  placeholder="e.g. Thomas (No.1)"
                  className={inputClass(false)}
                />
              )}
            </Field>
            <Field label="Date" hint="Leave blank to run every operating day">
              <select
                value={dateOverride}
                onChange={(e) => setDateOverride(e.target.value)}
                className={inputClass(false)}
              >
                <option value="">— every operating day —</option>
                {eachDate(event.startDate, event.endDate).map((d) => (
                  <option key={d} value={d}>{formatDateLabel(d)}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="From">
              {stations.length > 0 ? (
                <select
                  value={originStation}
                  onChange={(e) => setOriginStation(e.target.value)}
                  className={inputClass(false)}
                >
                  {stations.map((s) => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={originStation}
                  onChange={(e) => setOriginStation(e.target.value)}
                  className={inputClass(false)}
                />
              )}
            </Field>
            <Field label="To">
              {stations.length > 0 ? (
                <select
                  value={destinationStation}
                  onChange={(e) => setDestinationStation(e.target.value)}
                  className={inputClass(false)}
                >
                  {stations.map((s) => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={destinationStation}
                  onChange={(e) => setDestinationStation(e.target.value)}
                  className={inputClass(false)}
                />
              )}
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Capacity" error={errs.capacity}>
              <input
                type="number"
                min="0"
                step="1"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                className={`${inputClass(!!errs.capacity)} font-mono`}
              />
            </Field>
            <Field
              label="Booked"
              hint={merac.enabled ? 'Synced from MERAC' : 'Seeded — replaced by MERAC sync'}
              error={errs.booked}
            >
              <input
                type="number"
                min="0"
                step="1"
                value={booked}
                onChange={(e) => setBooked(e.target.value)}
                readOnly={merac.enabled}
                className={`${inputClass(!!errs.booked)} font-mono`}
              />
            </Field>
          </div>

          <Field label="Notes" hint="Internal">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className={`${inputClass(false)} resize-y`}
              placeholder="Optional — special stops, photo opportunities, exceptions."
            />
          </Field>

          {mut.isError && (
            <ServerError
              title={`Could not ${isEditing ? 'save' : 'add'} service`}
              error={mut.error as Error}
            />
          )}
        </div>
      }
    />
  )
}

function formatDateLabel(iso: string): string {
  const d = parseIsoDate(iso)
  return d.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  })
}
